import type { UploadFile } from "antd/es/upload/interface";

import type { BondDistributionFile } from "./types";

const REFUND_DISTRIBUTION_API_URL = (
  import.meta.env.VITE_REFUND_DISTRIBUTION_API_URL || ""
).trim();
const REFUND_DISTRIBUTION_API =
  REFUND_DISTRIBUTION_API_URL ||
  "/api/refund-distribution";
const REFUND_DISTRIBUTION_API_KEY =
  import.meta.env.VITE_REFUND_DISTRIBUTION_API_KEY || "";

interface RefundDistributionRecordMetadata {
  bondName: string;
  slotId: string;
  fileName: string;
  uploadedAt?: string;
}

interface RefundDistributionDownloadResponse {
  file?: BondDistributionFile;
}

const isAbsoluteHttpUrl = (value: string) =>
  /^https?:\/\//i.test(value);

const isLocalhostHost = (hostname: string) =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "0.0.0.0";

const shouldSkipBackendOnLocalhost = () =>
  typeof window !== "undefined" &&
  isLocalhostHost(window.location.hostname) &&
  !isAbsoluteHttpUrl(REFUND_DISTRIBUTION_API_URL);

export const isRefundDistributionBackendEnabled =
  () => !shouldSkipBackendOnLocalhost();

const apiFetch = async (
  path: string,
  options?: RequestInit
) => {
  const res = await fetch(
    `${REFUND_DISTRIBUTION_API}${path}`,
    {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(REFUND_DISTRIBUTION_API_KEY
        ? {
            Authorization: `Bearer ${REFUND_DISTRIBUTION_API_KEY}`,
          }
        : {}),
      ...options?.headers,
    },
    }
  );
  const payload = await res
    .json()
    .catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      String(
        (payload as { error?: string })?.error ||
          `Refund distribution API failed: ${res.status}`
      )
    );
  }
  return payload;
};

const isBondDistributionFile = (
  value: unknown
): value is BondDistributionFile => {
  if (
    typeof value !== "object" ||
    value === null ||
    Array.isArray(value)
  ) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.fileName === "string" &&
    typeof candidate.bondName === "string" &&
    Array.isArray(candidate.data)
  );
};

export const loadRefundDistributionFilesFromBackend =
  async (): Promise<{
    files: UploadFile[];
    data: BondDistributionFile[];
  }> => {
    if (shouldSkipBackendOnLocalhost()) {
      return { files: [], data: [] };
    }

    try {
      const fileList = (await apiFetch(
        "/files"
      )) as unknown;
      if (!Array.isArray(fileList)) {
        return { files: [], data: [] };
      }

      const downloads = await Promise.all(
        fileList.map(async (file) => {
          if (
            typeof file !== "object" ||
            file === null
          ) {
            return null;
          }

          const record =
            file as RefundDistributionRecordMetadata;
          if (
            typeof record.bondName !== "string" ||
            typeof record.slotId !== "string" ||
            typeof record.fileName !== "string"
          ) {
            return null;
          }

          const downloaded = (await apiFetch(
            `/download?bondName=${encodeURIComponent(
              record.bondName
            )}&slotId=${encodeURIComponent(
              record.slotId
            )}`
          )) as RefundDistributionDownloadResponse;

          try {
            const payload = downloaded?.file as unknown;
            if (!isBondDistributionFile(payload)) {
              return null;
            }
            return {
              file: {
                uid: `bond-distribution:${record.bondName}:${record.slotId}`,
                name: payload.fileName,
                status: "done",
              } as UploadFile,
              data: {
                ...payload,
                bondName:
                  payload.bondName || record.bondName,
                uploadSlotId:
                  payload.uploadSlotId || record.slotId,
              },
            };
          } catch (error) {
            console.error(
              "Failed to parse stored refund distribution payload",
              error
            );
            return null;
          }
        })
      );

      const valid = downloads.filter(Boolean) as Array<{
        file: UploadFile;
        data: BondDistributionFile;
      }>;

      return {
        files: valid.map((item) => item.file),
        data: valid.map((item) => item.data),
      };
    } catch (error) {
      console.error(
        "Failed to load refund distribution files from backend",
        error
      );
      return { files: [], data: [] };
    }
  };

export const saveRefundDistributionFileToBackend = async (
  file: BondDistributionFile
) => {
  if (shouldSkipBackendOnLocalhost()) {
    return "skipped" as const;
  }

  try {
    await apiFetch("/upload", {
      method: "POST",
      body: JSON.stringify({
        file,
      }),
    });
    return "saved" as const;
  } catch (error) {
    console.error(
      "Failed to save refund distribution file to backend",
      error
    );
    return "failed" as const;
  }
};
